<div class="cv-generate-panel" [nbSpinner]="initialLoading" nbSpinnerStatus="primary" nbSpinnerMessage="">
  <header class="panel-header">
    <div class="header-title">
      <h5>{{ 'cv.generate.title' | translate }}</h5>
      <p class="header-subtitle">{{ 'cv.generate.subtitle' | translate }}</p>
    </div>
    <button
      nbButton
      ghost
      status="basic"
      size="small"
      (click)="onClose()"
      [attr.aria-label]="'cv.generate.close' | translate"
      [nbTooltip]="'cv.generate.close' | translate">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </header>

  <!-- Mode fork: only entitled clients see the AI Tailor option. -->
  @if (canTailor()) {
    <div class="mode-switch">
      <nb-button-group size="small">
        <button
          nbButtonToggle
          type="button"
          [pressed]="mode === 'static'"
          (pressedChange)="$event && (mode = 'static')">
          {{ 'cv.tailor.mode.static' | translate }}
        </button>
        <button
          nbButtonToggle
          type="button"
          [pressed]="false"
          (pressedChange)="$event && flipToTailor()">
          {{ 'cv.tailor.mode.tailor' | translate }}
        </button>
      </nb-button-group>
    </div>
  }

  @if (mode === 'static') {
  <div class="panel-body">
    <div class="panel-grid" [class.panel-grid-loading]="initialLoading">

      <!-- Left: controls (~40%) -->
      <div class="panel-controls">
        @if (hasDefaults) {
          <form [formGroup]="form!" novalidate>

            <!-- Group: Template & language -->
            <div class="control-group">
              <div class="group-label">{{ 'cv.generate.groupTemplate' | translate }}</div>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.template' | translate }}</label>
                <nb-select
                  [selected]="selectedPickerValue"
                  (selectedChange)="onTemplatePickerChange($event)"
                  placeholder="{{ 'cv.generate.templatePlaceholder' | translate }}"
                  fullWidth>
                  <nb-option-group [title]="'cv.generate.builtInGroup' | translate">
                    @for (tpl of templates; track tpl.id) {
                      <nb-option [value]="'built_in:' + tpl.id">{{ tpl.name }}</nb-option>
                    }
                  </nb-option-group>
                  @for (client of clientsWithBranded; track client.id) {
                    <nb-option-group [title]="client.name">
                      @for (ct of brandedFor(client.id); track ct.id) {
                        <nb-option [value]="'client:' + ct.id">{{ ct.name }} ({{ ct.base_layout_id }})</nb-option>
                      }
                    </nb-option-group>
                  }
                </nb-select>
              </div>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.locale' | translate }}</label>
                <nb-select
                  formControlName="locale"
                  placeholder="{{ 'cv.generate.localePlaceholder' | translate }}"
                  fullWidth>
                  @for (loc of availableLocales; track loc) {
                    <nb-option [value]="loc">{{ localeLabel(loc) }}</nb-option>
                  }
                </nb-select>
              </div>
            </div>

            <!-- Group: Contact fields -->
            <div class="control-group">
              <div class="group-label">{{ 'cv.generate.groupContact' | translate }}</div>
              <div class="control-row">
                <div class="checkbox-list">
                  <nb-checkbox
                    [checked]="isContactChecked('email')"
                    (checkedChange)="toggleContact('email', $event)">
                    <span class="cv-opt-label">{{ 'cv.generate.contact.email' | translate }}</span>
                  </nb-checkbox>
                  <nb-checkbox
                    [checked]="isContactChecked('phone')"
                    (checkedChange)="toggleContact('phone', $event)">
                    <span class="cv-opt-label">{{ 'cv.generate.contact.phone' | translate }}</span>
                  </nb-checkbox>
                  <nb-checkbox
                    [checked]="isContactChecked('linkedin_url')"
                    (checkedChange)="toggleContact('linkedin_url', $event)">
                    <span class="cv-opt-label">{{ 'cv.generate.contact.linkedin' | translate }}</span>
                  </nb-checkbox>
                  <nb-checkbox
                    [checked]="isContactChecked('address')"
                    (checkedChange)="toggleContact('address', $event)">
                    <span class="cv-opt-label">{{ 'cv.generate.contact.address' | translate }}</span>
                  </nb-checkbox>
                </div>
              </div>
            </div>

            <!-- Group: Sections to include -->
            <div class="control-group">
              <div class="group-label">{{ 'cv.generate.groupSections' | translate }}</div>
              <p class="group-note">{{ 'cv.generate.sectionNote' | translate }}</p>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.section.experience' | translate }}</label>
                @if (experienceOptions.length > 0) {
                  <div class="checkbox-list">
                    @for (opt of experienceOptions; track opt.id) {
                      <nb-checkbox
                        [checked]="isChecked('experience_ids', opt.id)"
                        (checkedChange)="toggle('experience_ids', opt.id, $event)">
                        <span class="cv-opt-label">{{ opt.label }}</span>
                        @if (opt.meta) {
                          <span class="cv-opt-meta">{{ opt.meta }}</span>
                        }
                      </nb-checkbox>
                    }
                  </div>
                } @else {
                  <p class="cv-no-items">{{ 'cv.generate.noExperience' | translate }}</p>
                }
              </div>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.section.education' | translate }}</label>
                @if (educationOptions.length > 0) {
                  <div class="checkbox-list">
                    @for (opt of educationOptions; track opt.id) {
                      <nb-checkbox
                        [checked]="isChecked('education_ids', opt.id)"
                        (checkedChange)="toggle('education_ids', opt.id, $event)">
                        <span class="cv-opt-label">{{ opt.label }}</span>
                        @if (opt.meta) {
                          <span class="cv-opt-meta">{{ opt.meta }}</span>
                        }
                      </nb-checkbox>
                    }
                  </div>
                } @else {
                  <p class="cv-no-items">{{ 'cv.generate.noEducation' | translate }}</p>
                }
              </div>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.section.skills' | translate }}</label>
                @if (skillOptions.length > 0) {
                  <div class="checkbox-list">
                    @for (opt of skillOptions; track opt.id) {
                      <nb-checkbox
                        [checked]="isChecked('skill_ids', opt.id)"
                        (checkedChange)="toggle('skill_ids', opt.id, $event)">
                        <span class="cv-opt-label">{{ opt.label }}</span>
                        @if (opt.meta) {
                          <span class="cv-opt-meta">{{ opt.meta }}</span>
                        }
                      </nb-checkbox>
                    }
                  </div>
                } @else {
                  <p class="cv-no-items">{{ 'cv.generate.noSkills' | translate }}</p>
                }
              </div>

              <div class="control-row">
                <label class="label">{{ 'cv.generate.section.languages' | translate }}</label>
                @if (languageOptions.length > 0) {
                  <div class="checkbox-list">
                    @for (opt of languageOptions; track opt.id) {
                      <nb-checkbox
                        [checked]="isChecked('language_ids', opt.id)"
                        (checkedChange)="toggle('language_ids', opt.id, $event)">
                        <span class="cv-opt-label">{{ opt.label }}</span>
                        @if (opt.meta) {
                          <span class="cv-opt-meta">{{ opt.meta }}</span>
                        }
                      </nb-checkbox>
                    }
                  </div>
                } @else {
                  <p class="cv-no-items">{{ 'cv.generate.noLanguages' | translate }}</p>
                }
              </div>
            </div>

            <!-- Group: References -->
            <div class="control-group control-group-last">
              <div class="group-label">{{ 'cv.generate.groupReferences' | translate }}</div>

              <div class="control-row">
                <nb-checkbox
                  [checked]="includeReferences"
                  (checkedChange)="toggleReferences($event)">
                  {{ 'cv.generate.includeReferencesLabel' | translate }}
                </nb-checkbox>
              </div>

              @if (includeReferences) {
              <div class="control-row">
                <label class="label">{{ 'cv.generate.referencesTextLabel' | translate }}</label>
                <textarea
                  nbInput
                  fullWidth
                  rawpaste
                  formControlName="references_text"
                  rows="3"
                  placeholder="{{ 'cv.generate.referencesTextPlaceholder' | translate }}">
                </textarea>
              </div>
              }
            </div>
          </form>
        }
      </div>

      <!-- Right: live preview (~60%) -->
      <div class="panel-preview">
        <div class="preview-frame-wrapper" [class.is-loading]="previewLoading">
          @if (previewHtml) {
            <iframe class="preview-iframe" [srcdoc]="previewHtml" title="CV preview"></iframe>
          } @else if (!previewLoading) {
            <div class="preview-placeholder">
              <div class="preview-placeholder-icon">
                <nb-icon icon="file-text-outline"></nb-icon>
              </div>
              <p>{{ 'cv.generate.previewEmpty' | translate }}</p>
            </div>
          }

          @if (previewLoading) {
            <div class="preview-loading-overlay" [nbSpinner]="true" nbSpinnerStatus="primary" nbSpinnerSize="medium" nbSpinnerMessage="">
              <span class="preview-loading-label">{{ 'cv.generate.previewLoading' | translate }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <footer class="panel-footer">
    <button nbButton status="basic" (click)="onClose()" [disabled]="downloading">
      {{ 'cancel' | translate }}
    </button>
    <button
      nbButton
      status="primary"
      (click)="onGenerate()"
      [disabled]="downloading || !hasDefaults || initialLoading"
      [nbSpinner]="downloading"
      nbSpinnerStatus="control"
      nbSpinnerSize="tiny">
      <nb-icon icon="download-outline"></nb-icon>
      @if (downloading) {
        {{ 'cv.generate.downloading' | translate }}
      } @else {
        {{ 'cv.generate.downloadPDF' | translate }}
      }
    </button>
  </footer>
  }
</div>
